/**
 * SparkRenderer - Animated visual authentication display
 * 
 * Renders the Spark authentication pattern using the estream-spark WASM module.
 * ALL animation logic runs in Rust WASM - React just displays the pixel buffer.
 * 
 * ## Spark Variants (Classic Collection)
 * 
 * - Kaleidoscope: Symmetrical patterns with mirrored motion
 * - Orbital: Particles in circular orbits around center
 * - Hex: Hexagonal grid with pulsing cells
 * - Ember: Rising particles like floating embers
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import './SparkRenderer.css';

// WASM module type (loaded dynamically)
interface SparkWasmModule {
  WasmSparkRenderer: new (challengeJson: string, variant: string, size: number) => WasmSparkRenderer;
  spark_generate_challenge: (deviceIdHex: string) => string;
  spark_get_code: (challengeJson: string) => string;
}

interface WasmSparkRenderer {
  render_frame: (timeMs: bigint | number) => Uint8Array;
  size: () => number;
  base_hue: () => number;
  free: () => void;
}

// Spark variants - all 10 visual styles
export type SparkVariant = 
  // Core variants (7)
  | 'genesis'        // Original Lattice Code: 5 visual layers
  | 'orbital'        // Particles in circular orbits
  | 'constellation'  // Star-like connections
  | 'pulse'          // Rhythmic pulsing
  | 'ember'          // Rising ember particles
  | 'hex'            // Hexagonal grid
  | 'kaleidoscope'   // Symmetrical mirrored patterns
  // Accessibility variants (3)
  | 'highcontrast'   // Sharp edges, no gradients
  | 'inverted'       // Dark particles on light background
  | 'large';         // Fewer, bigger particles

export interface SparkRendererProps {
  /** Challenge JSON from the Spark hook */
  challengeJson?: string;
  /** Visual variant */
  variant?: SparkVariant;
  /** Canvas size in pixels */
  size?: number;
  /** Whether to show the Spark code overlay */
  showCode?: boolean;
  /** Custom class name */
  className?: string;
  /** Callback when renderer is ready */
  onReady?: () => void;
  /** Callback on error */
  onError?: (error: string) => void;
}

/**
 * SparkRenderer component
 * 
 * @example
 * ```tsx
 * <SparkRenderer
 *   challengeJson={challenge}
 *   variant="kaleidoscope"
 *   size={300}
 *   showCode
 * />
 * ```
 */
export function SparkRenderer({
  challengeJson,
  variant = 'kaleidoscope',
  size = 300,
  showCode = true,
  className = '',
  onReady,
  onError,
}: SparkRendererProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rendererRef = useRef<WasmSparkRenderer | null>(null);
  const animationRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);
  
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sparkCode, setSparkCode] = useState<string>('');
  const [wasmModule, setWasmModule] = useState<SparkWasmModule | null>(null);

  // Load WASM module
  useEffect(() => {
    let mounted = true;

    async function loadWasm() {
      try {
        // Fetch and evaluate the JS module from public folder
        const jsResponse = await fetch('/pkg-spark/estream_spark.js');
        const jsCode = await jsResponse.text();
        
        // Create a blob URL to import the JS module
        const blob = new Blob([jsCode], { type: 'application/javascript' });
        const blobUrl = URL.createObjectURL(blob);
        
        // Dynamic import from blob URL
        const module = await import(/* @vite-ignore */ blobUrl) as { default: (input?: unknown) => Promise<void> } & SparkWasmModule;
        URL.revokeObjectURL(blobUrl);
        
        // Initialize WASM
        const wasmResponse = await fetch('/pkg-spark/estream_spark_bg.wasm');
        const wasmBytes = await wasmResponse.arrayBuffer();
        await module.default(wasmBytes);
        
        if (mounted) {
          setWasmModule(module);
          setIsLoading(false);
        }
      } catch (e) {
        const errorMsg = e instanceof Error ? e.message : 'Failed to load WASM';
        console.error('[Spark] WASM load error:', e);
        if (mounted) {
          setError(errorMsg);
          setIsLoading(false);
          onError?.(errorMsg);
        }
      }
    }

    loadWasm();
    return () => { mounted = false; };
  }, [onError]);

  // Initialize renderer when challenge changes
  useEffect(() => {
    if (!wasmModule || !challengeJson) return;

    try {
      // Clean up previous renderer
      if (rendererRef.current) {
        rendererRef.current.free();
        rendererRef.current = null;
      }

      // Create new renderer
      const renderer = new wasmModule.WasmSparkRenderer(challengeJson, variant, size);
      rendererRef.current = renderer;

      // Extract spark code for display
      try {
        const code = wasmModule.spark_get_code(challengeJson);
        setSparkCode(code);
      } catch {
        // Fallback: extract from challenge JSON
        const parsed = JSON.parse(challengeJson);
        setSparkCode(parsed.spark_code || '');
      }

      startTimeRef.current = performance.now();
      onReady?.();
    } catch (e) {
      const errorMsg = e instanceof Error ? e.message : 'Failed to create renderer';
      console.error('[Spark] Renderer creation error:', e);
      setError(errorMsg);
      onError?.(errorMsg);
    }
  }, [wasmModule, challengeJson, variant, size, onReady, onError]);

  // Animation loop
  const animate = useCallback(() => {
    const canvas = canvasRef.current;
    const renderer = rendererRef.current;
    if (!canvas || !renderer) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Calculate elapsed time
    const elapsed = performance.now() - startTimeRef.current;
    
    // Render frame in WASM
    const pixels = renderer.render_frame(BigInt(Math.floor(elapsed)));
    const frameSize = renderer.size();

    // Create ImageData and blit to canvas
    const imageData = new ImageData(
      new Uint8ClampedArray(pixels),
      frameSize,
      frameSize
    );
    ctx.putImageData(imageData, 0, 0);

    // Continue animation
    animationRef.current = requestAnimationFrame(animate);
  }, []);

  // Start/stop animation
  useEffect(() => {
    if (rendererRef.current && canvasRef.current) {
      animationRef.current = requestAnimationFrame(animate);
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [animate, wasmModule, challengeJson]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (rendererRef.current) {
        rendererRef.current.free();
      }
    };
  }, []);

  if (isLoading) {
    return (
      <div className={`spark-renderer spark-loading ${className}`}>
        <div className="spark-spinner" />
        <span>Loading Spark...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`spark-renderer spark-error ${className}`}>
        <span className="spark-error-icon">⚠️</span>
        <span>{error}</span>
      </div>
    );
  }

  return (
    <div className={`spark-renderer ${className}`}>
      <div className="spark-canvas-container">
        <canvas
          ref={canvasRef}
          width={size}
          height={size}
          className="spark-canvas"
        />
        {showCode && sparkCode && (
          <div className="spark-code-overlay">
            <span className="spark-code">{sparkCode}</span>
          </div>
        )}
      </div>
      <div className="spark-variant-label">{variant}</div>
    </div>
  );
}

/**
 * Variant selector component
 */
export interface VariantSelectorProps {
  value: SparkVariant;
  onChange: (variant: SparkVariant) => void;
  className?: string;
}

export function SparkVariantSelector({ value, onChange, className = '' }: VariantSelectorProps) {
  const coreVariants: Array<{ id: SparkVariant; name: string; description: string }> = [
    { id: 'genesis', name: 'Genesis', description: 'Original Lattice Code with 5 visual layers' },
    { id: 'orbital', name: 'Orbital', description: 'Particles in circular orbits' },
    { id: 'constellation', name: 'Constellation', description: 'Star-like particle connections' },
    { id: 'pulse', name: 'Pulse', description: 'Rhythmic pulsing animation' },
    { id: 'ember', name: 'Ember', description: 'Rising ember particles' },
    { id: 'hex', name: 'Hex', description: 'Hexagonal grid with pulses' },
    { id: 'kaleidoscope', name: 'Kaleidoscope', description: 'Symmetrical mirrored patterns' },
  ];

  const accessibilityVariants: Array<{ id: SparkVariant; name: string; description: string }> = [
    { id: 'highcontrast', name: 'High Contrast', description: 'Sharp edges, no gradients' },
    { id: 'inverted', name: 'Inverted', description: 'Dark particles on light background' },
    { id: 'large', name: 'Large', description: 'Fewer, bigger particles for distance' },
  ];

  return (
    <div className={`spark-variant-selector ${className}`}>
      <div className="variant-group">
        <span className="variant-group-label">Core</span>
        <div className="variant-buttons">
          {coreVariants.map(v => (
            <button
              key={v.id}
              className={`variant-btn ${value === v.id ? 'active' : ''}`}
              onClick={() => onChange(v.id)}
              title={v.description}
            >
              {v.name}
            </button>
          ))}
        </div>
      </div>
      <div className="variant-group">
        <span className="variant-group-label">Accessibility</span>
        <div className="variant-buttons">
          {accessibilityVariants.map(v => (
            <button
              key={v.id}
              className={`variant-btn accessibility ${value === v.id ? 'active' : ''}`}
              onClick={() => onChange(v.id)}
              title={v.description}
            >
              {v.name}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default SparkRenderer;
